import { useFormikContext } from 'formik'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface FormikValues {
  readonly mentorId: string
  readonly studentId: string
}

export default function AdministratorPortfolioStudentAddPage() {
  return (
    <SuspenseWrapper>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent() {
  const [successfullySubmitted, setSuccessfullySubmitted] = React.useState(false)
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()

  const initialValues = React.useMemo<FormikValues>(
    () => ({
      mentorId: '',
      studentId: '',
    }),
    []
  )

  const handleSubmit = React.useCallback(
    async (values: FormikValues): Promise<void> => {
      await Api.postAdministratorPortfolioStudentsAssign({
        headers,
        body: {
          mentorId: Number(values.mentorId),
          studentId: Number(values.studentId),
        },
      })

      toast.success(t('common:updated_successfully'))
      setSuccessfullySubmitted(true)
    },
    [headers, t]
  )

  const redirectPath = `/${locale}/administrator/portfolio/students`

  React.useEffect(() => {
    if (successfullySubmitted) {
      navigate(redirectPath)
    }
  }, [successfullySubmitted, navigate, redirectPath])

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: t('portfolio:portfolio'), path: `/${locale}/administrator/portfolio` },
      { page: t('student:students'), path: `/${locale}/administrator/portfolio/students` },
      { page: t('common:add'), path: `/${locale}/administrator/portfolio/students/add` },
    ],
    [t, locale]
  )

  return (
    <Section
      title={`${t('portfolio:portfolio')} / ${t('student:students')} / ${t('common:add')}`}
      icon={<PortfolioIcon />}
      breadcrubms={breadcrumbsItems}
      rightElement={<BackButton />}
    >
      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        classNames={{ form: 'w-full' }}
        isConfirmable={!successfullySubmitted}
      >
        <FormContent />
      </Form>
    </Section>
  )
}

function FormContent(): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const formik = useFormikContext<FormikValues>()

  const { data: mentors } = useApi({
    endpoint: Api.getAdministratorPortfolioListsPortfolioMentors,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const { data: students } = useApi({
    endpoint: Api.getAdministratorPortfolioListsStudents,
    params: React.useMemo(() => ({ headers, query: { sectionId: '4' } }), [headers]),
  })

  const mentorOptions = React.useMemo(
    () => mentors?.map((m) => ({ value: m.id.toString(), label: m.name })) ?? [],
    [mentors]
  )

  const studentOptions = React.useMemo(
    () => students?.map((s) => ({ value: s.id.toString(), label: `${s.name} (${s.persNum})` })) ?? [],
    [students]
  )

  return (
    <div className="mt-6 w-full rounded-card bg-card px-6 py-9">
      <FormError />
      <div className="mb-4">
        <SelectInput name="mentorId" label={t('portfolio:mentor')} options={mentorOptions} required />
      </div>
      <div className="mb-4">
        <SelectInput name="studentId" label={t('student:student')} options={studentOptions} required />
      </div>
      <div className="mt-6 flex flex-wrap justify-end">
        <Button type="submit" variant="red" disabled={!formik.isValid || !formik.dirty}>
          {t('common:save')}
        </Button>
      </div>
    </div>
  )
}
